import { IconWarning } from './components/icons';
import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import SignIn from './pages/SignIn';
import Dashboard from './pages/Dashboard';
import PatientManagement from './pages/PatientManagement';
import SMSCampaign from './pages/SMSCampaign';
import ChannelCampaigns from './pages/ChannelCampaigns';
import VoiceCalls from './pages/VoiceCalls';
import VoiceSMS from './pages/VoiceSMS';
import Chatbot from './pages/Chatbot';
import Testing from './pages/Testing';
import ReviewDashboard from './pages/ReviewDashboard';
import ReviewDetail from './pages/ReviewDetail';
import ContentLibrary from './pages/ContentLibrary';
import Analytics from './pages/Analytics';
import ScheduledCampaigns from './pages/ScheduledCampaigns';
import AuthCallback from './pages/AuthCallback';
import { isApiConfigured } from './config/api';

// ─── Error Boundary ──────────────────────────────────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('[ErrorBoundary] Uncaught error:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-xl shadow-lg border border-red-200 max-w-md w-full p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <IconWarning className="h-8 w-8 text-red-500" weight="regular" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h2>
            <p className="text-gray-600 text-sm mb-2">
              An unexpected error occurred in the application.
            </p>
            {this.state.error && (
              <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded p-2 mb-4 font-mono text-left overflow-auto max-h-24">
                {this.state.error.message}
              </p>
            )}
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium transition-colors"
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── App ─────────────────────────────────────────────────────────────────────
function App() {

  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <Routes>
            {/* Public routes */}
            <Route path="/sign-in" element={<SignIn />} />
            {/* Handles Supabase email-confirmation redirect (PKCE code exchange) */}
            <Route path="/auth/callback" element={<AuthCallback />} />

            {/* Protected routes */}
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<Dashboard />} />
                      <Route path="/patients" element={<PatientManagement />} />
                      <Route path="/sms" element={<SMSCampaign />} />
                      <Route path="/smart-sms" element={<ChannelCampaigns channelType="smart_sms" />} />
                      <Route path="/voice-sms" element={<ChannelCampaigns channelType="voice_sms" />} />
                      {/* Send a recorded broadcast now. Like the bulk-call
                          page, this shipped with no route, so the only way
                          to reach Voice SMS was the scheduler. */}
                      <Route path="/voice-sms/send" element={<VoiceSMS />} />
                      <Route path="/voice-calls" element={<ChannelCampaigns channelType="voice_calls" />} />
                      {/* Immediate bulk outbound dialling. This page shipped
                          with no route at all, so /elevenlabs/calls/bulk-outbound
                          was unreachable from the running app. */}
                      <Route path="/voice-calls/bulk" element={<VoiceCalls />} />
                      <Route path="/chatbot" element={<Chatbot />} />
                      <Route path="/testing" element={<Testing />} />
                      <Route path="/review-queue" element={<ReviewDashboard />} />
                      <Route path="/reviews" element={<ContentLibrary />} />
                      <Route path="/review/:reviewId" element={<ReviewDetail />} />
                      <Route path="/analytics" element={<Analytics />} />
                      <Route path="/scheduled" element={<ScheduledCampaigns />} />
                    </Routes>
                  </Layout>
                </ProtectedRoute>
              }
            />
          </Routes>
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
